import { ExploreInsightCondition, ExploreInsightDirection } from '@api';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import clsx from 'clsx';
import { SyntheticEvent } from 'react';

import { InsightIcons } from '../insight-icons/InsightIcons';
import { getInsightIconColorAndDirection } from '../insight-icons/utils';
import styles from './InsightIconsRoot.module.scss';

export interface InsightIconsRootProps {
  conditions: ExploreInsightCondition[];
  direction: ExploreInsightDirection;
  isStarred: boolean;
  customSize?: { width: string; height: string };
  isCombined?: boolean;
  isMatched?: boolean;
  layout?: 'vertical' | 'horizontal';
  isSparklesCondensed?: boolean;
  onIconClick?: (e: SyntheticEvent) => void;
  source?: PosthogTrackingSource;
  insightId?: string;
  entities?: string[];
  themeIds?: string[];
}

export const InsightIconsRoot = ({
  conditions,
  direction,
  isStarred,
  customSize,
  isCombined = false,
  isMatched = true,
  layout,
  isSparklesCondensed,
  onIconClick,
  source,
  insightId,
  entities,
  themeIds,
}: InsightIconsRootProps) => {
  const { trackInsightIconClicked } = usePosthogEvents();

  const handleClick = (e: SyntheticEvent) => {
    if (!onIconClick) {
      return;
    }

    if (source && insightId) {
      trackInsightIconClicked({
        source,
        type: 'scenario',
        insightId,
        ...(entities !== undefined && { entities }),
        ...(themeIds !== undefined && { themeIds }),
      });
    }

    onIconClick(e);
  };

  const wrapperClassName = clsx(styles.insightIconsWrapper, {
    [styles.disabled]: !isMatched,
  });

  const sparkleSize = isSparklesCondensed ? 6 : 12;

  if (isCombined) {
    const { names, style, iconDirection } = getInsightIconColorAndDirection({
      conditions,
      matched: isMatched,
      direction,
      layout,
    });

    return (
      <div
        className={wrapperClassName}
        data-testid="insight-icons-root"
        onClick={handleClick}
      >
        {isStarred && (
          <img
            className={styles.styledImg}
            src="/app/static/images/icons/sparkles.svg"
            data-testid="sparkles-svg"
            width={sparkleSize}
            height={sparkleSize}
          />
        )}
        <InsightIcons
          iconNames={names}
          variant={style}
          direction={iconDirection}
          customSize={customSize}
        />
      </div>
    );
  }

  return (
    <>
      {conditions.map((condition, index) => {
        const { names, style, iconDirection } = getInsightIconColorAndDirection(
          {
            conditions,
            matched: isMatched,
            direction,
            layout: undefined,
          }
        );
        return (
          <div
            className={wrapperClassName}
            data-testid="insight-icons-root"
            key={`${condition.method}-${index}`}
            onClick={handleClick}
          >
            {isStarred && (
              <img
                className={styles.styledImg}
                src="/app/static/images/icons/sparkles.svg"
                data-testid="sparkles-svg"
                width={sparkleSize}
                height={sparkleSize}
              />
            )}
            <InsightIcons
              iconNames={names}
              variant={style}
              direction={iconDirection}
              customSize={customSize}
            />
          </div>
        );
      })}
    </>
  );
};
